import React, { useState, useEffect } from 'react';
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Icon,
  Popover,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  LinearProgress,
  MenuList,
  Paper,
  Popper,
  CircularProgress,
  Select,
  MenuItem,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { get, size, last, uniqBy, keyBy, flatten, set, first, take, unset } from 'global/lib/lodash';
import Confirm from 'global/components/confirm';
import { getBase64Image, sortAlphaNum } from 'global/utils';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import sendData from 'redux/actions/sendData';
import clsx from 'clsx';
import highlightedText from 'global/components/highlight-text';
import { useStyles } from './styles';
import useDebounce from '../../hooks/useDebounce';
import { useScreen } from 'global/styles';
import useImageColumns from 'global/hooks/useImageColumns';
import CheckBoxOutlineBlankOutlined from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBox from '@mui/icons-material/CheckBox';
import { cleanCopy, downloadImage, stopProp, _size } from '../../utils';
import { awaitAll } from '../../utils/awaitAll';
import { Button } from '../Button';
import { FilterBar, FilterButton, FilterGroupToggle, FilterRating, FilterReset, FilterSearch, photoTagToRating, ratingToPhotoTag } from 'global/components/AssessmentFilters';
import { Loader } from '../../styles';
import { PopContainer } from '../more-drop';
import { getRealmIcon } from '../droprealms';
import FolderIcon from '@mui/icons-material/Folder';
import qs from 'qs';
import { pressedKeys } from '../../utils/keytracker';
import { useIntersect } from '../../hooks/useIntersect';
import { PhotosGrid } from '../perform-grid';
import SnackAlerts from '../snack-alerts';
import NavToTop from '../../../feature/nav/NavToTop';
import { Media } from '../Media';
import { Text } from '../text';
import { Checkbox } from '../checkbox';
import { useSocket } from '../../hooks/useSocket';
import { useUser } from '../../hooks/useUser';

const defaultUpdates = {
  quality: undefined,
  location: 0,
  supersets: [],
  somethingChanged: false,
};

const takeAdd = 20;

const ImageGrid = ({ supersets = [], realms = [], ...props }) => {
  const { userCanEdit, uploadPhotos, download, loading, canWallterSubscore, handleWallterAutoTag } = props;
  const folders = get(props, 'folders', []);
  const includeSearch = get(props, 'includeSearch', false);
  let [chosenFolders, setChosenFolders] = useState([]);
  const [items, _setItems] = useState([]);
  const [allItems, _setAllItems] = useState([]);
  const [popOpen, setPopOpen] = useState();
  const [confirm, setConfirm] = useState();
  const [dlStats, setDlStats] = useState();
  const [searchVal, setSearchVal] = useState('');
  const [alerts, setAlerts] = useState();
  const [wallterLoading, setWallterLoading] = useState(false);
  const [takeAmt, setTakeAmt] = useState(takeAdd);
  const [searchStat, setSearchStat] = useState({ isSearching: false, hasSearched: false });
  const [selected, setSelected] = useState({});
  const [filts, setFilts] = useState({
    cat: 'location',
    tag: undefined,
  });
  const [autoTagOptionIndex, setAutoTagOptionIndex] = useState(2);
  const [autoTagAnchorEl, setAutoTagAnchorEl] = useState(null);
  const [updates, _setUpdates] = useState(cleanCopy(defaultUpdates));
  const classes = useStyles();
  const debouncedSearch = useDebounce(searchVal, 750);
  const [ref, entry] = useIntersect({});
  const [btnRef, btnEntry] = useIntersect({});
  const inView = get(entry, 'isIntersecting', false);
  const [ctxtBtnsFixed, setCtxtBtnsFixed] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [allphotos, setAllPhotos] = useState([]);
  const [state, setState] = useState({
    showNot: false,
  });

  const { isMobile } = useScreen();
  const { me } = useUser();
  const userId = get(me, 'id');

  const catOptions = [
    { title: 'By Location', id: 'location' },
    { title: 'By Category', id: 'system' },
  ];
  const defaultCat = 'location';

  const isFolders = get(items, '0.photos');

  const setItems = (its, sort = true) => {
    const it = uniqBy(its, 'id');
    _setItems(sort ? it.sort(sortAlphaNum) : it);
  };

  useEffect(() => {
    setCtxtBtnsFixed(!btnEntry.isIntersecting);
  }, [btnEntry.isIntersecting]);

  // This minor delay makes the following hook get called only after items are rendered.
  // It makes inView (bool visibility of the observer) more accurate when it's called.
  const _takeAmt = useDebounce(takeAmt, 50);
  useEffect(() => {
    if (takeAmt == 0 || (!loading && inView && takeAmt < size(items))) {
      setTakeAmt(takeAmt + takeAdd);
    }
  }, [inView, _takeAmt]);

  const setAllItems = (its) => {
    _setAllItems(uniqBy(its, 'id'));
    // PFM-2703: reset pagination to the first page directly instead of dipping
    // through 0. Setting takeAmt to 0 left `take(items, 0)` rendering an empty
    // grid until a debounced effect bumped it back to takeAdd; on large photo
    // sets that recovery could lose the race (or a quick filter toggle landed in
    // the gap), leaving the Photos tab blank even though `items` was populated.
    setTakeAmt(takeAdd);
  };

  const filterTaggedItems = (its = allItems, setTheItems = true) => {
    const _items = its.filter((it) => {
      if (it.photos) {
        return loading ? false : true;
      }
      let passes = true;
      if (filts.tag !== undefined && !get(it, 'divider')) {
        const itemQual = get(it, 'quality');
        passes = filts.tag === 'both' ? itemQual !== null : itemQual === filts.tag;
      }
      return passes;
    });

    if (setTheItems) {
      setItems(_items, false);
    } else {
      return _items;
    }
  };

  useEffect(() => {
    if (get(allItems, '0.photos')) {
      filterTaggedItems();
    }
  }, [allItems]);

  // PFM-2709: `folders` arrives in two shapes — grouped folder objects (each
  // with a `.photos` array, used by the Photos tab) OR a flat list of photo
  // objects (the scores/highlights photo picker rendered via ImageModal).
  // PFM-2703 began deriving the canonical photo set with
  // `flatten(folders.map((f) => f.photos))`, which assumes the grouped shape and
  // collapses the flat-list shape to an empty array — so rating filters (thumbs
  // up / thumbs down) matched nothing and the grid reported "no photos here".
  // Handle both: use a folder's `.photos` when present, otherwise treat the
  // entry itself as a photo.
  const collectPhotos = (fldrs = folders) => uniqBy(flatten((fldrs || []).map((f) => (f && f.photos ? f.photos : f))).filter(Boolean), (a) => a.id);

  const setupFolders = (clear = false) => {
    let searchfor = folders;
    const allPhotos = collectPhotos();
    const isFiltered = debouncedSearch || filts.tag !== undefined;
    if (isFiltered) {
      setAllItems(allPhotos);
      setFilteredItems(allPhotos);
    } else if (filts.cat === 'system') {
      const map = keyBy(
        supersets.map((s) => ({ ...s, photos: undefined })),
        'id'
      );
      map['null'] = {
        realm: { name: 'None' },
        icon: FolderIcon,
        photos: [],
      };
      allPhotos.map((p) => {
        if (!size(p.supersets)) {
          set(map, ['null', 'photos'], [...get(map, ['null', 'photos'], []), p]);
        }
        (p.supersets || []).map((s) => {
          set(map, [s.id, 'realm'], s);
          set(map, [s.id, 'icon'], FolderIcon);
          set(map, [s.id, 'photos'], [...get(map, [s.id, 'photos'], []), p]);
        });
      });
      const its = Object.keys(map)
        .filter((k) => !!size(get(map, [k, 'photos'])))
        .map((k) => ({
          ...map[k],
          id: `realm-${k}`,
        }));
      setAllItems(its);
      // setStateFolders(its);
      setItems(its);
      setChosenFolders();
      searchfor = its;
    } else {
      const all = folders; // [ ...folders, { divider: { text: 'All Photos' } }, ...allPhotos ];
      setAllItems(all);
      setItems(all, false);
      setChosenFolders();
    }

    setAllPhotos(allPhotos);

    if (size(chosenFolders) && !clear) {
      const found = searchfor.find((f) => f.id === get(chosenFolders, '0.id'));
      !!found && clickImage(found, -1);
    }
  };

  useEffect(() => {
    setupFolders();
  }, [folders]);

  const updateSelected = async () => {
    const sel = Object.keys(selected)
      .filter((k) => selected[k])
      .map((k) => selected[k]);

    const results = await Promise.all(
      sel.map(async (item, index) => {
        try {
          const realm_id = updates.location != 0 ? get(updates, 'location', item.realm.id) : item.realm.id;
          const quality = get(updates, 'quality', item.quality);

          const relationships = {};

          const toSet = [];
          updates.supersets.map((s) => {
            if (s.all) {
              toSet.push({ type: 'supersets', id: s.id });
            } else if (!s.all && item.supersets.find((its) => its.id == s.id)) {
              toSet.push({ type: 'supersets', id: s.id });
            }
          });

          set(relationships, 'supersets.data', toSet);

          const data = {
            data: {
              type: 'realm-photos',
              id: item.id,
              attributes: {
                name: get(item, 'name'),
                assessment_id: get(item, 'assessment.id'),
                realm_id,
                quality,
                batchSize: size(sel),
                batchIndex: index,
              },
              relationships,
            },
          };

          const resp = await sendData(
            {
              url: `/realm-photos/${item.id}?${qs.stringify({ include: ['supersets', 'realm', 'images'].join(',') })}`,
              method: 'patch',
              data,
            },
            { normalize: { build: ['realmPhotos'] } }
          );
          return resp[0];
        } catch (err) {
          setAlerts({ message: `Patch failed: ${err.message}`, severity: 'error' });
        }
      })
    );
    const saved = results.filter((photo) => photo && photo.id != null);
    if (size(saved)) {
      if (props.onPhotosUpdated) {
        props.onPhotosUpdated(saved);
      } else {
        await update();
      }
      _setUpdates(cleanCopy(defaultUpdates));
      setSelected({});
      setState((s) => ({ ...s, showNot: false }));
      setAlerts({ message: 'Photo updates saved' });
    }
  };

  const setUpdates = (up) => {
    _setUpdates((u) => {
      const n = typeof up === 'function' ? up(u) : up;
      return {
        ...n,
        somethingChanged: true,
      };
    });
  };

  useEffect(() => {
    if (!listOpen && updates.somethingChanged) {
      updateSelected();
    }
  }, [updates, listOpen]);

  useEffect(() => {
    props.onSelectedChange && props.onSelectedChange(selected);
  }, [selected]);

  const clickImage = (img, index = 0) => {
    if (img.photos) {
      //is actually folder
      setItems(img.photos);
      setAllItems(img.photos);
      setChosenFolders([img]);
      if (index !== -1) {
        const upd = cleanCopy(defaultUpdates);
        _setUpdates(upd);
        setSelected({});
      } else if (_size(selected)) {
        //remove from selected images that are no longer in the set
        const sel = {};
        img.photos.map((p) => {
          if (selected[p.id]) {
            set(sel, p.id, p);
          }
        });
        setSelected(sel);
      }
    }
  };

  const clearFolder = (index) => {
    setDlStats((s) => ({ ...s, isLoading: true }));
    const folds = [...chosenFolders];
    folds.splice(index, 1);
    chosenFolders = folds;
    setChosenFolders(folds);
    setupFolders(true);
    setSelected({});
    setTakeAmt(takeAdd);
    setState((s) => ({ ...s, showNot: false }));

    setTimeout(() => {
      setDlStats((s) => ({ ...s, isLoading: false }));
    }, 500);
  };

  // const deleteImage = async (e, img) => {
  //   stopProp(e);
  //   const origItems = [...items];
  //   try {
  //     const itemIndex = items.findIndex((it) => it.id === img.id);
  //     items.splice(itemIndex, 1);
  //     const newItems = [...items];
  //     setItems(newItems);
  //     await sendData({
  //       url: `/realm-photos/${img.id}`,
  //       method: 'patch',
  //       data: {
  //         data: {
  //           id: img.id,
  //           type: 'realm-photos',
  //           attributes: {
  //             archived: true,
  //           },
  //         },
  //       },
  //     });
  //     update();
  //   } catch (err) {
  //     console.error(err);
  //     setItems(origItems);
  //     setAlerts({
  //       message: 'Delete Failed',
  //       severity: 'error',
  //     });
  //   }
  // };

  const selectImage = (e, img) => {
    stopProp(e);
    const shiftIsSelected = !!get(pressedKeys, '16');
    if (shiftIsSelected && !!_size(selected)) {
      const selIndexes = [];
      items.map((item, i) => {
        if (item.id == img.id || selected[item.id]) {
          selIndexes.push(i);
        }
      });
      const sorted = selIndexes.sort((a, b) => a - b);
      const newselected = {};
      for (let i = first(sorted); i <= last(sorted); i++) {
        const item = items[i];
        newselected[item.id] = item;
      }
      setSelected(newselected);
    } else {
      setSelected((s) => {
        if (s[img.id]) {
          unset(s, img.id);
        } else {
          set(s, img.id, img);
        }
        return { ...s };
      });
    }
  };

  const update = async () => {
    await get(props, 'update', () => 0)();
  };

  const folderOption = (e, folder, index) => {
    stopProp(e);
    setPopOpen({ index, anchor: e.currentTarget });
  };

  const setFilteredItems = (all = allItems) => {
    all = all.sort((a, b) => {
      const amt = size(a.supersets);
      const bmt = size(b.supersets);

      return amt - bmt;
    });
    let _items = [];
    const sups = {};
    const v = (debouncedSearch || '').toLowerCase();
    const checks = ['name', 'realm.name', ['photos', 'supersets.0.name'], ['photos', 'name']];
    const isSup = get(filts, 'cat') != 'location';

    // PFM-2709: distinguish the two `folders` shapes. The Photos tab passes
    // grouped folder objects (each with a `.photos` array) and `realm` loaded,
    // so location grouping headers are meaningful. The scores/highlights photo
    // picker (ImageModal) passes a FLAT list of photos with no `realm` included,
    // so the location-grouping path below produced a bogus "None" header when a
    // rating filter was applied. In that flat picker, skip the grouping headers
    // entirely and render a clean flat grid (matching its unfiltered view).
    const isFlatList = size(folders) > 0 && !folders.some((f) => f && f.photos);

    const addAndCheck = (item) => {
      const cat = get(filts, 'cat') == 'location' ? 'realm' : 'supersets.0';

      if (isSup) {
        (!size(item.supersets) ? [{ id: -1, name: 'None' }] : item.supersets).map((s) => {
          const cf = get(chosenFolders, '0');
          if (cf && get(cf, 'systemId')) {
            if (get(cf, 'systemId') == s.id) {
              set(sups, s.id, { ...s, items: [...get(sups, [s.id, 'items'], []), item] });
            }
          } else {
            set(sups, s.id, { ...s, items: [...get(sups, [s.id, 'items'], []), item] });
          }
        });
        return;
      }

      const pastItem = last(_items);
      const pastFolder = get(pastItem, `${cat}.id`, 0);
      const thisFolder = get(item, `${cat}.id`, 0);
      if (!isFlatList && (pastFolder !== thisFolder || pastFolder === 0)) {
        _items.push({ id: get(item, `${cat}.name`, 0), divider: { text: get(item, `${cat}.name`, 'None') } });
      }
      _items.push(item);
    };

    all.map((item) => {
      if (debouncedSearch) {
        for (let i = 0; i < checks.length; i++) {
          const c = checks[i];
          if (Array.isArray(c)) {
            // eslint-disable-next-line no-loop-func
            const [first, ...rest] = c;
            get(item, first, []).map((ii) => {
              const val = get(ii, ...rest, '').toLowerCase();
              if (val.indexOf(v) > -1) {
                addAndCheck({
                  ...ii,
                  parentFolder: {
                    ...item,
                  },
                });
              }
            });
          } else {
            if (get(item, c, '').toLowerCase().indexOf(v) > -1) {
              addAndCheck(item);
            }
          }
        }
      } else {
        addAndCheck(item);
      }
    });

    if (isSup) {
      Object.keys(sups).map((k) => {
        const its = filterTaggedItems(get(sups, [k, 'items'], []), false);
        if (size(its)) {
          const sub = get(sups, `${k}.name`, 'None');
          _items.push({ id: k, divider: { text: sub } });
          // PFM-2703: a photo tagged to multiple systems is intentionally listed
          // under each system group, so its `id` is NOT unique across the flat
          // list. Stamp a group-scoped `_key` (systemId + photoId) so the grid
          // renders unique React keys. Without this, duplicate keys corrupted
          // React's reconciliation, leaving orphaned photo tiles stranded among
          // the folders (and stray photos persisting after returning to "ALL").
          its.map((it) => _items.push({ ...it, sub, _key: `s${k}_${get(it, 'id')}` }));
        }
      });
      _setItems(_items);
    } else {
      _items = _items.sort((a, b) => {
        const aPhotos = get(a, 'photos', []);
        const bPhotos = get(b, 'photos', []);
        return aPhotos.length > bPhotos.length ? -1 : 1;
      });

      if (filts.tag !== undefined) {
        filterTaggedItems(_items);
      } else {
        setItems(_items, false);
      }
    }

    setSearchStat({ isSearching: false, hasSearched: true });
  };

  const toggleNot = () => {
    const useNot = !state.showNot;

    if (useNot) {
      const notListed = allphotos.filter((a) => !items.find((it) => it.id == a.id));

      const cfname = get(chosenFolders, '0.name');

      _setItems([{ divider: { text: `Not ${cfname}` } }, ...notListed, { divider: { text: cfname } }, ...items]);
    } else {
      _setItems(get(chosenFolders, '0.photos', []));
    }

    setState((f) => ({ ...f, showNot: useNot }));
  };

  useEffect(() => {
    const isNotFiltered = !debouncedSearch && filts.tag === undefined;
    // PFM-2703: always derive the canonical photo set from the `folders`
    // source-of-truth rather than from the current `items` view. `items` may be
    // a filtered subset (a flat, rating-filtered list or a partial folder list),
    // so flattening it dropped photos when toggling filters/grouping.
    // PFM-2709: `collectPhotos` handles both folder shapes (grouped vs flat).
    const _allItems = collectPhotos();

    if (isNotFiltered && !get(chosenFolders, '0')) {
      setSearchStat({ isSearching: false, hasSearched: false });
      // setupFolders() fully rebuilds both `items` and `allItems` for the active
      // grouping and is the single source of truth here. Previously this branch
      // ALSO called setItems(_allItems) afterwards — a second, conflicting write
      // that left the grid inconsistent (blank, or a stray flat photo mixed in
      // with the folders) until a downstream effect happened to correct it.
      return setupFolders(true);
    }

    setAllItems(_allItems);
    setTakeAmt(takeAdd);
    setFilteredItems(_allItems);
  }, [debouncedSearch, filts]);

  useSocket(
    {
      channel: `wallter.${userId}`,
      onEvent: (data) => {
        setWallterLoading(false);
        setAlerts({ message: 'Auto-Tagging Complete', severity: 'success' });
        update();
      },
    },
    [`wallter.${userId}`]
  );

  const changeSearch = (val) => {
    setSearchVal(val);
    setSearchStat((ds) => ({ ...ds, isSearching: true }));
  };

  const photosFiltersDefault = !searchVal && filts.cat === defaultCat && filts.tag === undefined && !state.showNot;

  const resetPhotoFilters = () => {
    const hadNot = state.showNot;
    setSearchVal('');
    setSearchStat({ isSearching: false, hasSearched: false });
    setFilts({ cat: defaultCat, tag: undefined });
    setState((s) => ({ ...s, showNot: false }));
    if (hadNot) {
      _setItems(get(chosenFolders, '0.photos', []));
    }
  };

  const closePop = (e) => {
    stopProp(e);
    setPopOpen();
  };

  const confirmDeleteAll = async (img) => {
    setDlStats((dl) => ({
      ...dl,
      isLoading: true,
      id: get(img, 'realm.id'),
      progress: 0,
    }));
    setConfirm();

    const files = get(img, 'photos', []);
    const failed = [];
    await awaitAll(
      files.map((p, index) => () => async () => {
        try {
          await sendData({
            url: `/realm-photos/${p.id}`,
            method: 'patch',
            data: {
              data: {
                id: p.id,
                type: 'realm-photos',
                attributes: {
                  archived: true,
                  batchSize: size(files),
                  batchIndex: index,
                },
              },
            },
          });
          setDlStats((dl) => ({
            ...dl,
            progress: dl.progress + 100 / files.length,
          }));
        } catch (err) {
          failed.push(p);
        }
      }),
      { queueSize: 10 }
    );

    setDlStats();

    if (failed.length > 0) {
      setAlerts({
        message: `These photos failed to delete: ${failed.map((f) => f.name).join(', ')}`,
        severity: 'error',
      });
    } else {
      setAlerts({
        message: 'Delete Success',
        severity: 'success',
      });
    }

    setSelected({});
    update();
  };

  const confirmDownload = async (img) => {
    setDlStats((dl) => ({
      ...dl,
      isLoading: true,
      id: get(img, 'realm.id'),
      progress: 0,
    }));
    setConfirm();

    const files = get(img, 'photos', []);

    if (size(files) === 1) {
      await downloadImage(files[0]);
      return setDlStats();
    }

    const b64Array = await Promise.all(
      files.map(async (f) => {
        try {
          const data = await getBase64Image(f.fullUrl);
          setDlStats((dl) => ({
            ...dl,
            progress: dl.progress + 100 / files.length,
          }));
          return {
            ...f,
            data,
          };
        } catch (err) {
          return null;
        }
      })
    );

    const zip = new JSZip();
    b64Array
      .filter((f) => f)
      .forEach((file) => {
        zip.file(file.name, file.data, { base64: true });
      });

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${get(img, 'realm.name', get(img, 'realm.id'))}-photos.zip`);
      setDlStats();
    });
  };

  const onDeleteSelected = (e) => {
    closePop(e);
    setConfirm({
      title: 'Delete Selected Images?',
      content: `Are you sure you want to delete the selected images?`,
      onOk: () =>
        confirmDeleteAll({
          realm: { id: 'selectedDelete' },
          photos: items.filter((item) => !!get(selected, item.id)),
        }),
      onCancel: () => setConfirm(),
    });
  };

  const onDeleteAll = (e, img) => {
    closePop(e);
    setConfirm({
      title: 'Delete All Images?',
      content: `Are you sure you want to delete all images in ${img.realm.name}?`,
      onOk: () => confirmDeleteAll(img),
      onCancel: () => setConfirm(),
    });
  };

  const onDownloadAll = (e, img) => {
    closePop(e);
    setConfirm({
      title: 'Download All Images?',
      content: `Are you sure you want to download all images in ${img.realm.name}?`,
      onOk: () => confirmDownload(img),
      onCancel: () => setConfirm(),
    });
  };

  const updateSuperset = (supe) => {
    let sups = [...get(updates, 'supersets', [])];
    const sui = sups.findIndex((s) => s.id == supe.id);
    const su = sups[sui];

    if (!su) {
      sups.push({ id: supe.id, all: true });
    } else if (get(su, 'all') == false) {
      set(sups, `${sui}.all`, true);
    } else {
      sups.splice(sui, 1);
    }
    setUpdates((u) => ({ ...u, supersets: sups }));
  };

  const renderChosen = () => {
    return (chosenFolders || []).map((cf, i) => (
      <Button key={cf.id} size="small" onClick={() => clearFolder(i)}>
        <div className={classes.chosenFolder}>
          <p className={classes.cfTitle}>{get(cf, 'realm.name', '')}</p>
          <p className={classes.cfSize}>{`${size(items.filter((i) => !get(i, 'divider')))} photos`}</p>
          <IconButton variant="text" className={classes.closeFolder} size="large">
            <Icon>close</Icon>
          </IconButton>
        </div>
      </Button>
    ));
  };

  const blankContent = () => {
    if (props.blankContent) {
      return props.blankContent();
    }

    return null;
  };

  const openList = () => {
    const sups = {};

    Object.keys(selected).map((k) => {
      const sel = selected[k];

      const selsupersets = sel.supersets;

      selsupersets.map((s) => set(sups, s.id, [...get(sups, s.id, []), s]));
    });

    const usets = [...get(updates, 'supersets', [])];
    Object.keys(sups).map((k) => {
      const val = sups[k];
      usets.push({ id: k, all: size(val) >= size(Object.keys(selected)) });
    });

    _setUpdates((u) => ({ ...u, supersets: usets }));
    setListOpen(true);
  };

  const closeList = () => {
    setListOpen(false);
    setTimeout(() => {
      _setUpdates(cleanCopy(defaultUpdates));
    }, 50);
  };

  const selectedSize = size(Object.keys(selected).filter((k) => selected[k]));

  const showCtxt = userCanEdit && !!selectedSize;

  const canSelect = userCanEdit || props.canSelect;

  const cols = useImageColumns({ inModal: props.inModal, maxColumns: props.maxColumns });

  const folderIsSystem = !!get(chosenFolders, '0.systemId');
  const autoTagOptions = [
    { count: 1, menuLabel: 'Up to 1 category' },
    { count: 2, menuLabel: 'Up to 2 categories' },
    { count: 3, menuLabel: 'Up to 3 categories' },
  ];
  const selectedAutoTagCount = get(autoTagOptions, [autoTagOptionIndex, 'count'], 3);
  const autoTagMenuOpen = Boolean(autoTagAnchorEl);

  const sortImgSupersets = (img) => {
    if (!img.supersets) {
      return null;
    }

    if (folderIsSystem) {
      const sysId = get(chosenFolders, '0.systemId');
      let newsets = [];

      img.supersets.map((s) => {
        if (s.id == sysId) {
          newsets = [s, ...newsets];
        } else {
          newsets.push(s);
        }
      });

      return newsets;
    }

    return img.supersets;
  };

  const handleWallterAutoTagRequested = async () => {
    if (wallterLoading) return;
    setWallterLoading(true);
    try {
      handleWallterAutoTag && (await handleWallterAutoTag(selectedAutoTagCount));
    } finally {
      setWallterLoading(false);
    }
  };

  const handleAutoTagToggleClick = (event) => {
    setAutoTagAnchorEl(event.currentTarget);
  };

  const handleAutoTagMenuClose = () => {
    setAutoTagAnchorEl(null);
  };

  const handleAutoTagMenuItemClick = (index) => {
    setAutoTagOptionIndex(index);
    handleAutoTagMenuClose();
  };

  return (
    <>
      {userCanEdit && !_size(selected) && (
        <div className={classes.buttonRow}>
          <Button className={classes.button} variant="contained" color="secondary" onClick={uploadPhotos} startIcon={<Icon>cloud_upload</Icon>}>
            {isMobile ? 'Upload' : 'Upload Photos'}
          </Button>
          <Button className={classes.button} variant="contained" color="secondary" onClick={download} startIcon={<Icon>cloud_download</Icon>}>
            {'Download'}
            {props.dlStats ? <CircularProgress variant="determinate" size={16} value={props.dlStats.progress} /> : null}
          </Button>
          {canWallterSubscore && (
            <>
              <ButtonGroup className={classes.splitButtonGroup}>
                <Button
                  className={classes.splitButtonPrimary}
                  variant="outlined"
                  onClick={handleWallterAutoTagRequested}
                  startIcon={<Icon>photo_filter</Icon>}
                  loading={wallterLoading}
                  disabled={wallterLoading}
                >
                  {`Auto-Tag (${selectedAutoTagCount})`}
                </Button>
                <Button className={classes.splitButtonToggle} variant="outlined" size="small" onClick={handleAutoTagToggleClick} aria-label="Select auto-tag mode" disabled={wallterLoading}>
                  <Icon>arrow_drop_down</Icon>
                </Button>
              </ButtonGroup>
              <Popper open={autoTagMenuOpen} anchorEl={autoTagAnchorEl} transition className={classes.popper}>
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleAutoTagMenuClose}>
                        <MenuList id="auto-tag-split-button-menu">
                          {autoTagOptions.map((option, index) => (
                            <MenuItem key={option.count} selected={index === autoTagOptionIndex} onClick={() => handleAutoTagMenuItemClick(index)}>
                              {option.menuLabel}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </>
          )}
        </div>
      )}
      <div ref={btnRef} />
      {showCtxt && (
        <div className={clsx(classes.selectedRow, ctxtBtnsFixed && classes.selectRowFixed)}>
          <PhotosGrid>
            <div className={classes.row}>
              <IconButton onClick={() => setSelected({})} size="large">
                <Icon>close</Icon>
              </IconButton>
              <div>{`${selectedSize} Selected`}</div>
              <div className={classes.selButtonRow}>
                <PopContainer
                  icon="label"
                  onOpen={openList}
                  onClose={closeList}
                  title={
                    <>
                      <Icon style={{ marginRight: 5 }}>label</Icon>Update Tags
                    </>
                  }
                  options={supersets.map((r) => ({ title: r.name, selected: get(updates, 'supersets', []).indexOf(r.id) > -1, onClick: () => updateSuperset(r) }))}
                >
                  <div className="popContainer">
                    <div className="spaceVert">
                      <Text bold>Quality</Text>

                      <ToggleButtonGroup exclusive value={updates.quality} onChange={(e, q) => setUpdates((v) => ({ ...v, quality: q }))}>
                        <ToggleButton value={null}>
                          <Icon>close</Icon>
                        </ToggleButton>
                        <ToggleButton value={1}>
                          <Icon>thumb_up</Icon>
                        </ToggleButton>
                        <ToggleButton value={0}>
                          <Icon>thumb_down</Icon>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>

                    <div className="spaceVert">
                      <Text bold>Location</Text>

                      <Select size="small" fullWidth placeholder={'Select a Location'} value={updates.location} onChange={(e) => setUpdates((v) => ({ ...v, location: e.target.value }))}>
                        <MenuItem value={0}>Any</MenuItem>
                        {realms.map((l) => (
                          <MenuItem value={l.id}>{l.name}</MenuItem>
                        ))}
                      </Select>
                    </div>

                    <div className="spaceVert">
                      <Text bold>Category</Text>

                      <div>
                        {supersets.map((s) => (
                          <Checkbox
                            checkProps={{
                              size: 'small',
                              indeterminate:
                                get(
                                  updates.supersets.find((su) => su.id == s.id),
                                  'all'
                                ) == false,
                            }}
                            checked={
                              !!get(
                                updates.supersets.find((su) => su.id == s.id),
                                'all'
                              )
                            }
                            onChange={() => updateSuperset(s)}
                            label={s.name}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </PopContainer>
                <div className={classes.vr} />
                <Button
                  variant="contained"
                  color="notice"
                  onClick={() => confirmDownload({ realm: { id: `Selected-${new Date().toISOString()}` }, photos: items.filter((item) => !!get(selected, item.id)) })}
                  disableElevation
                  size="small"
                  startIcon={get(dlStats, 'id', '').indexOf('Selected-') > -1 ? <Loader size={14} /> : <Icon>save_alt</Icon>}
                  className={clsx(classes.selButton)}
                >
                  Download
                </Button>
                <div className={classes.vr} />
                <Button
                  variant="contained"
                  onClick={onDeleteSelected}
                  disableElevation
                  size="small"
                  startIcon={get(dlStats, 'id') === 'selectedDelete' ? <Loader size={14} /> : <Icon>delete</Icon>}
                  color="error"
                  className={clsx(classes.selButton, classes.selDelete)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </PhotosGrid>
        </div>
      )}

      <FilterBar sx={{ mt: showCtxt ? (isMobile ? '150px' : '80px') : undefined }}>
        <FilterReset visible={!photosFiltersDefault} onClick={resetPhotoFilters} />
        {includeSearch && (
          <FilterSearch
            searchString={searchVal}
            onChange={changeSearch}
            onClear={() => changeSearch('')}
            loading={get(searchStat, 'isSearching', false)}
          />
        )}
        {!props.hideSuperset && (
          <FilterGroupToggle
            value={filts.cat}
            defaultValue={defaultCat}
            options={catOptions}
            onChange={(cat) => setFilts((f) => ({ ...f, cat }))}
          />
        )}
        <FilterRating
          expanded
          value={photoTagToRating(filts.tag)}
          onChange={(rating) => setFilts((f) => ({ ...f, tag: ratingToPhotoTag(rating) }))}
        />
        {folderIsSystem && (
          <FilterButton active={state.showNot} onClick={toggleNot} aria-label="Show All">
            Show All
          </FilterButton>
        )}
      </FilterBar>

      <div className={classes.foldersRow}>{!isMobile && renderChosen()}</div>

      {isMobile && <div className={classes.foldersRow}>{renderChosen()}</div>}

      {(props.loading || get(dlStats, 'isLoading')) && <LinearProgress color="primary" />}
      <NavToTop visible={ctxtBtnsFixed} />
      <ImageList className={classes.gridList} cols={cols} gap={16}>
        {!get(searchStat, 'isSearching') &&
          !get(dlStats, 'isLoading') &&
          !props.loading &&
          take(items, takeAmt).map((img, i) => {
            const isFolder = get(img, 'photos');
            const prevImg = get(items, [i - 1]);
            const prevImage = get(prevImg, 'photos');
            const divide = !isFolder && prevImage && searchVal && !get(searchStat, 'isSearching');
            const divider = get(img, 'divider');
            const HasIcon = get(img, 'icon');
            const isSelected = !!get(selected, img.id);

            const imageSel = isSelected;

            return divider ? (
              <ImageListItem cols={cols} key={`divider_${i}`} className={classes.hrDivider}>
                <div>{divider.text}</div>
              </ImageListItem>
            ) : (
              <ImageListItem
                className={clsx(isFolder ? classes.gridListFolder : classes.gridTile, divide && classes.divider, imageSel && classes.gridTileSelected)}
                onClick={!!selectedSize ? (e) => selectImage(e, img) : () => (isFolder ? clickImage(img, i) : undefined)}
                key={get(img, '_key', img.id)}
              >
                <>
                  {HasIcon ? (
                    <HasIcon className={classes.imgContainer} />
                  ) : (
                    <Media
                      {...img}
                      viewOnly={!userCanEdit}
                      canEdit={!selectedSize}
                      opt={'tag'}
                      images={items}
                      index={i}
                      onPhotoUpdated={props.onPhotosUpdated}
                      onClose={(s) => (s.hasEdits && !props.onPhotosUpdated ? update() : undefined)}
                    />
                  )}

                  {!isFolder && (
                    <>
                      {canSelect && (
                        <IconButton
                          classes={{ root: classes.iconRoot }}
                          onClick={(e) => selectImage(e, img)}
                          className={clsx(classes.checkIcon, imageSel && classes.checkIconSelected, !!selectedSize && classes.checkIconSelectMode)}
                          size="large"
                        >
                          {imageSel ? <CheckBox /> : <CheckBoxOutlineBlankOutlined />}
                        </IconButton>
                      )}

                      <PhotoText img={{ ...img, supersets: sortImgSupersets(img) }} isSelected={imageSel} />
                    </>
                  )}

                  {isFolder && (
                    <ImageListItemBar
                      title={highlightedText(`${get(img, 'realm.name', '')}`, searchVal)}
                      subtitle={<span>{`${size(img.photos)} photos`}</span>}
                      classes={{ root: classes.titleBar }}
                      actionIcon={
                        !userCanEdit ? undefined : (
                          <>
                            {dlStats && dlStats.id === get(img, 'realm.id') ? (
                              <IconButton className={classes.icon} size="large">
                                <CircularProgress variant="determinate" size={22} value={dlStats.progress} />
                              </IconButton>
                            ) : (
                              <IconButton onClick={(e) => folderOption(e, img, i)} aria-label={`info about ${img.name}`} className={classes.icon} size="large">
                                <Icon>more_vert</Icon>
                              </IconButton>
                            )}
                            <Popover className={classes.popOver} open={get(popOpen, 'index') === i} anchorEl={get(popOpen, 'anchor')} onClose={closePop}>
                              <ButtonGroup orientation="vertical" variant="contained" color="secondary">
                                {userCanEdit && (
                                  <Button startIcon={<Icon>delete</Icon>} onClick={(e) => onDeleteAll(e, img)}>
                                    Delete All
                                  </Button>
                                )}
                                <Button startIcon={<Icon>cloud_download</Icon>} onClick={(e) => onDownloadAll(e, img)}>
                                  Download
                                </Button>
                              </ButtonGroup>
                            </Popover>
                          </>
                        )
                      }
                    />
                  )}
                </>
              </ImageListItem>
            );
          })}
        {!get(searchStat, 'isSearching') && size(items) === 0 && !props.loading && blankContent()}
        <Box sx={{ height: 20 }} ref={ref} />
      </ImageList>

      <Confirm {...confirm} />

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </>
  );
};

const PhotoText = (props) => {
  const { img, isSelected } = props;
  const classes = useStyles();

  const style = get(props, 'style', {});

  const hasQuality = get(img, 'quality') !== null && get(img, 'quality') !== undefined;
  const isBp = hasQuality && img.quality === 1;

  let icon = hasQuality ? <Icon className={isBp ? 'pos' : 'neg'}>{isBp ? 'thumb_up' : 'thumb_down'}</Icon> : getRealmIcon(img.realm);
  let title = get(img, 'name');
  let subtitle = get(img, 'sub', img.supersets ? img.supersets.map((s) => s.name).join('; ') : get(img, 'realm.name'));

  return (
    <div id={'photoText'} className={clsx(classes.photoText, isSelected && classes.photoTextSelected)} style={style}>
      <div className="icon">{icon}</div>
      <div className="textContainer">
        <div className="title">{title}</div>
        {!!subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
    </div>
  );
};

export default ImageGrid;
